import Animation from 'animation'
import * as scale from 'd3-scale'
import * as d3 from 'd3-selection'
import Tooltip from 'tooltip'
import tooltipContents from 'tooltip/contents'
import { EPSILON } from 'chart-constants'
import * as colors from 'colors'

const minFontSize = 6
const maxFontSize = 32
const leafPadding = 3 // 3 pixels on each side
const nonLeafPadding = 5

const minWidth = 40

const crop = (selection, width, padding) => {
  selection.each(function (d) {
    if (width < minWidth) {
      return // don't waste time croping, we're not showing it
    }

    while (Math.floor(this.getComputedTextLength()) > width - padding * 2) {
      d3.select(this).text(`${d3.select(this).text().slice(0, -4)}...`)
    }
  })

}

/*
 * Return the closest ancestor that's drillable
 */
const getDrillableAncestor = d => {
  return d.ancestors().find(ancestor => {
    return ancestor.data.drilldown
  })
}

/*
 * Treemap animation
 */
class TreeMapAnimation extends Animation {

  match () {
    return true
  }

  configure (axis, serie) {
    super.configure(axis, serie)

    let color = this.chart.getPropertyValue(`--palette-color-${serie.color}`)
      , domain0 = 0
      , domain1 = serie.totalDepth + 1 // so it's one shade before white

    if (this.showRoot) {
      // The show root setting is on, which means when they are zoomed all the
      // way out, we show a wrapper node. That wrapper node will have a `level` of -1
      // Add one extra layer of scale
      domain0 = -1
    }

    this.colorScale = scale.scaleLinear()
                           .domain([domain0, domain1])
                           .range([color, '#FFFFFF'])

    if (this.series.node()) {
      this.previousColorScale = this.series.node().parentNode.__colorScale
    }

    this._labels = this._labels.bind(this)

    return this
  }

  _labels (context) {
    let selection = context.selection ? context.selection() : context
      , dimensions = this.dimensions
      , scale = this.colorScale
      , placeholder = d3.select(selection.node())
                        .append('text')
                        .attr('class', 'label label-placeholder')

    selection.each(function (d) {
      let fontSize = '14px' // default
        , text = d3.select(this)
                    .select('.label')
        , color = scale(d.data.level)

      text.classed('is-capitalized', d => d.depth % 2 === 0) // alternate capitalized headers
          .classed('leaf', d => !d.children)
          .classed('light dark', false)

      if (color) {
        text.classed(colors.getTextVariation(color), true)
      }

      // used to calculate the final state of the label. Needed since
      // this runs within a transition
      placeholder.style('font-size', '1px')
                 .text(d.data.title)
                 .classed('is-capitalized', text.classed('is-capitalized'))

      if (!d.children) {
        // Leaf node, determine font size
        let width = (d.x1 - d.x0) - leafPadding * 2
          , bb = placeholder.node().getBBox()
          , size = Math.min(width / bb.width, maxFontSize)

        size = Math.max(minFontSize, size)
        fontSize = `${size}px`
      }

      placeholder.style('font-size', fontSize)
                 .call(crop, d.x1 - d.x0, d.children ? nonLeafPadding : leafPadding)

      if (context !== selection) {
        text = text.transition(context)
      }

      text.node()._fontSize = fontSize // Store the final font size in case an animation wants it

      text.text(placeholder.text())
          .style('display', '') // show all
          .style('font-size', fontSize)
          .select(function () {
            // Return the parent node which is the g.text-group so we can position it, rather than the text node
            return this.parentNode
          })
          .attr('transform', function (d) {
            let transform = ''
            if (d.depth === 0) {
              let x = 10
                , y = 5

              // Root label offset
              transform = `translate(${x + dimensions.rootOffset},${y})`
            } else if (!d.children) {
              // Leaf nodes
              let y = (d.y1 - d.y0) / 2
                , containerWidth = d.x1 - d.x0
                , textWidth = placeholder.node().getComputedTextLength()
                , x = Math.max(leafPadding, (containerWidth - textWidth) / 2)

              transform = `translate(${x}, ${y})`
            } else {
              // non leaf nodes
              transform = `translate(${nonLeafPadding}, ${dimensions.outerPadding / 2})`
            }
            this._transform = transform // Store final location
            return transform
          })
          .filter(d => (d.x1 - d.x0) < minWidth || (d.y1 - d.y0) < 20) // Too small, don't show the
          .style('display', 'none')
    })

    placeholder.remove()
  }

  _getTooltip (node, d, svg, dimensions, formatter) {
    let container = svg.getBoundingClientRect()
      , scale = svg.getBoundingClientRect().width / parseInt(d3.select(svg).style('width'))
      , box = {
        right: container.right,
        left: container.left,
        top: container.top + dimensions.wrapperPadding
      }

    if (dimensions.wrapperPadding) {
      box.left += dimensions.margin.left
      box.right -= dimensions.margin.right
      box.top += dimensions.margin.top
    }

    let tooltip = new Tooltip(d3.select(this.chart.node().parentNode).attr('id') + '-tooltip')
      , xScaled = (d.x0 + (d.x1 - d.x0) / 2) * scale
      , x = this.chart.layout.rtl ? box.right - xScaled : box.left + xScaled
      , y = box.top + (d.y0 + (d.y1 - d.y0) / 2) * scale
      , rows = [{
          cols: [{
            text: formatter(d.value)
          }],
          header: [d.data.title]
        }]

    if (d.children) {
      // Position this on the top bar if the block contains children
      y = box.top + (10 + (d.y0 + 0)) * scale // half the padding
    }

    tooltip._html(tooltipContents(rows, this.chart))
           ._position(x, y)

    this.hoverContainer.node().__tooltip = tooltip

    return tooltip
  }

  bindAndAnimate (container, node) {
    let serie = d3.select(node).datum()
      , isExiting = d3.select(node).classed('serie-exit')
      , svg = d3.select(node.ownerSVGElement)
      , clip = `#${svg.attr('id')}-chart-clip-path-plot-height-${this.plotId}`
      , chartDimensions = this.chart.datum().dimensions
      , dimensions = this.dimensions
      , blockContainer = d3.select(node)
                           .attr('clip-path', `url(${clip})`) // Clip the serie container.
                           .selectAll('g.tree-block-container').data(d => [d])

    this.hoverContainer = container.selectAll('g.hover-container').selection()
                                   .data(d => [d])
                                   .enter()
                                     .append('g').attr('class', 'hover-container')
                                     .merge(container.selectAll('g.hover-container'))

    // the default height based clip path isn't accurate enough for the
    // animations. This forces the height clip path into a confined box
    // resulting in much cleaner drill animations.
    d3.select(clip)
      .selectAll('rect')
      .transition()
      .duration(container.duration())
      .attr('y', 0)
      .attr('height', chartDimensions.height)

    blockContainer = blockContainer.enter()
                                   .append('g')
                                     .attr('class', 'tree-block-container')
                                     .attr('transform', `translate(${-this.dimensions.padding}, ${-this.dimensions.padding + this.dimensions.wrapperPadding})`)
                                     .merge(blockContainer)
    // Add the root element wrapper element used to show extra padding on the
    // root node
    let wrapper = blockContainer.selectAll('rect.wrapper')
                                .data(d => this.showRoot && !isExiting ? [d] : [])

    wrapper.enter()
           .append('rect')
           .attr('class', 'wrapper')
           .attr('height', chartDimensions.height + this.dimensions.wrapperPadding + this.dimensions.padding)
           .attr('width', chartDimensions.width + this.dimensions.outerPadding)
           .attr('y', 0)
           .style('opacity', EPSILON)
           .lower()
           .merge(wrapper)
           .transition(container)
           .attr('y', -this.dimensions.wrapperPadding)
           .attr('fill', serie.level !== undefined ? this.colorScale(serie.level) : this.colorScale.range()[0])
           .attr('height', chartDimensions.height)
           .attr('width', chartDimensions.width)
           .style('opacity', 1)

    wrapper.exit()
           .transition(container)
           .attr('y', this.dimensions.padding)
           .attr('width', chartDimensions.width + this.dimensions.padding)
           .style('opacity', EPSILON)
           .remove()

    let group = blockContainer.selectAll('g.treemap-block')
                              .data(isExiting ? [] : this.blocks, d => d.data.id)
      , enter = group.enter()
                     .append('g')
                     .attr('class', 'treemap-block')
      , exit = group.exit().transition(container)
      , transition = enter.merge(group)
                          .transition(container)
      , selection = transition.selection()
      , formatter = serie.formatter || (d => d)
      , self = this

    enter.append('rect')
    enter.append('g')
         .attr('class', 'text-group')
         .append('text')
         .attr('class', 'label label-on-colored-background')
         .text(d => d.data.title)

    enter.on('click', (e, d) => {
      let node = null

      if (d.data.drilldown) {
        // They can drilldown on this node, use it.
        node = d
      }

      if (!node) {
        node = getDrillableAncestor(d)
      }

      if (node) {
        let point = node.data._point
          , range = node.data._range

        this.chart.ee.emit('chart-event', 'select:point', point, range)
      }

    })
    .on('mouseenter', function (e, d) {
      let svg = blockContainer.node().ownerSVGElement

      self._tooltip = self._getTooltip(this, d, svg, dimensions, formatter).show()

      if (d.data.drilldown) {
        d3.select(this).classed('is-hover is-clickable', true)
      } else if (d.parent) {
        let drillableAncestor = getDrillableAncestor(d)

        if (drillableAncestor) {
          d3.select(this).classed('is-clickable', true)

          selection.filter(node => node == drillableAncestor)
                   .classed('is-hover', true)
        }
      }
    })
    .on('mouseleave', function () {
      if (self._tooltip) {
        self._tooltip.destroy()
        self._tooltip = null
      }
      selection.classed('is-hover is-clickable', false) // Remove from all nodes
    })

    // Move the container into the correct location for the padding
    blockContainer.transition(container)
                  .attr('transform', `translate(${-this.dimensions.padding}, ${-this.dimensions.padding + this.dimensions.wrapperPadding})`)

    selection.classed('no-drilldown', d => !d.data.drilldown)
             .filter((d, i) => i === 0)
             .style('fill-opacity', 0) // Root node doesn't have a background color. That's controlled by the wrapper

    this.animate(serie, enter, group, transition, exit)

    this.series.node().parentNode.__colorScale = this.colorScale
  }

}

export default TreeMapAnimation
